import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  Button,
  Badge,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FiCreditCard, FiPlus, FiTrash2, FiStar, FiStarOff, FiLock } from "react-icons/fi";
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  useGetPaymentMethods, 
  useAddPaymentMethod, 
  useSetDefaultPaymentMethod, 
  useDeletePaymentMethod 
} from "../../services/settingsService";
import CardElementComponent from "../../components/stripe/CardElement";

const PaymentMethodsSection = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useToast();

  // Stripe hooks
  const stripe = useStripe();
  const elements = useElements();

  // Fetch payment methods
  const { paymentMethodsData, paymentMethodsIsLoading, refetchPaymentMethods } = useGetPaymentMethods();
  const { addPaymentMethodPayload, addPaymentMethodIsLoading } = useAddPaymentMethod(() => {
    refetchPaymentMethods();
    onClose();
    setCardError(null);
    setIsProcessing(false);
  });
  const { setDefaultPaymentMethodPayload, setDefaultPaymentMethodIsLoading } = useSetDefaultPaymentMethod(() => {
    refetchPaymentMethods();
  });
  const { deletePaymentMethodPayload, deletePaymentMethodIsLoading } = useDeletePaymentMethod(() => {
    refetchPaymentMethods();
    setIsDeleteDialogOpen(false);
    setMethodToDelete(null);
  });

  const handleAddPaymentMethod = () => {
    setCardError(null);
    setIsProcessing(false);
    onOpen();
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      toast({
        title: "Error",
        description: "Stripe has not loaded yet. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsProcessing(true);
    setCardError(null);

    try {
      // Create payment method from card element
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement('card'),
      });

      if (error) {
        setCardError(error.message);
        setIsProcessing(false);
        return;
      }

      // Send payment method ID to backend
      await addPaymentMethodPayload({
        payment_method_id: paymentMethod.id
      });

    } catch (error) {
      setCardError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    await setDefaultPaymentMethodPayload(methodId);
  };

  const handleDelete = (method) => {
    setMethodToDelete(method);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (methodToDelete) {
      await deletePaymentMethodPayload(methodToDelete.id);
    }
  };

  const formatCardNumber = (last4) => {
    return `•••• •••• •••• ${last4}`;
  };

  const getCardBrandColor = (brand) => {
    const colors = {
      visa: "blue",
      mastercard: "red",
      amex: "green",
      discover: "orange",
    };
    return colors[brand?.toLowerCase()] || "gray";
  };

  if (paymentMethodsIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color="gray.600">Loading payment methods...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Subscription Payment Methods
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Manage your payment methods for recurring subscription payments.
          </Text>
        </VStack>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={handleAddPaymentMethod}
        >
          Add Payment Method
        </Button>
      </HStack>

      {/* Payment Methods List */}
      <Card>
        <CardBody>
          {paymentMethodsData?.length > 0 ? (
            <VStack spacing={4} align="stretch">
              {paymentMethodsData.map((method) => (
                <Box
                  key={method.id}
                  p={4}
                  border="1px solid"
                  borderColor={method.is_default ? "blue.200" : "gray.200"}
                  borderRadius="md"
                  bg={method.is_default ? "blue.50" : "white"}
                >
                  <HStack justify="space-between" align="center">
                    <HStack spacing={4}>
                      <Box
                        p={2}
                        borderRadius="md"
                        bg={`${getCardBrandColor(method.card?.brand)}.100`}
                        color={`${getCardBrandColor(method.card?.brand)}.600`}
                      >
                        <FiCreditCard size={20} />
                      </Box>
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2}>
                          <Text fontWeight="500">
                            {method.card?.brand?.toUpperCase()} {formatCardNumber(method.card?.last4)}
                          </Text>
                          {method.is_default && (
                            <Badge colorScheme="blue" variant="subtle" size="sm">
                              Default
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          Expires {method.card?.exp_month}/{method.card?.exp_year}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={2}>
                      {!method.is_default && (
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<FiStar />}
                          onClick={() => handleSetDefault(method.id)}
                          isLoading={setDefaultPaymentMethodIsLoading}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        leftIcon={<FiTrash2 />}
                        onClick={() => handleDelete(method)}
                        isDisabled={method.is_default}
                      >
                        Remove
                      </Button>
                    </HStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          ) : (
            <Box textAlign="center" py={12}>
              <FiCreditCard size={64} color="gray.300" />
              <Text mt={4} fontSize="lg" fontWeight="500" color="gray.700">
                No Payment Methods
              </Text>
              <Text mt={2} color="gray.600">
                Add a payment method to get started
              </Text>
              <Button
                mt={4}
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={handleAddPaymentMethod}
              >
                Add Payment Method
              </Button>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Add Payment Method Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <FiLock />
              <Text>Add Payment Method</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Secure Payment</AlertTitle>
                  <AlertDescription>
                    Your card information is encrypted and secure. We never store your card details on our servers.
                  </AlertDescription>
                </Box>
              </Alert>
              
              <FormControl>
                <FormLabel>Card Information</FormLabel>
                <CardElementComponent 
                  error={cardError} 
                  setError={setCardError}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={addPaymentMethodIsLoading || isProcessing}
              isDisabled={!stripe || isProcessing}
            >
              {isProcessing ? "Processing..." : "Add Payment Method"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Payment Method
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this payment method? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={deletePaymentMethodIsLoading}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default PaymentMethodsSection; 